import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Select,
  Space,
  Spin,
  Tag,
  Timeline as AntTimeline,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { formatDateTime, JsonBlock } from '../Assessments/shared';
import { TimelineDocument, TimelineEvent } from './actionTypes';
import { fetchTimeline } from './actionsApi';
import { readableError } from './errors';
import { describeActor } from './Jobs';

/**
 * The chronological audit of one blueprint family: capture, curation import,
 * every mint, every publish, the benchmark pushes, the gate verdicts, the
 * activation, the launches and the scored sessions — plus every job for that
 * family that did NOT succeed.
 *
 * A successful job emits no row of its own: it already left a durable record
 * (a mint, a publish, a push, an activation) and that record carries the job
 * id. Emitting both would double every successful action in the one view
 * whose entire value is a single column of time, and the failures are exactly
 * what has no other record today.
 */

const SEVERITY_COLOR: Record<string, string> = {
  ok: 'blue',
  warn: 'orange',
  error: 'red',
};

const KIND_LABEL: Record<string, string> = {
  draft_captured: 'Draft captured',
  curation_imported: 'Curation imported',
  mint: 'Minted',
  version_published: 'Version published',
  version_locked: 'Version locked',
  benchmark_pushed: 'Answer key pushed',
  gate_computed: 'Validity gate computed',
  scenario_activated: 'Assessment activated',
  session_launched: 'Session launched',
  assessment_queued: 'Assessment queued',
  assessment_scored: 'Assessment scored',
  assessment_failed: 'Assessment failed',
  job_refused: 'Job refused',
  job_failed: 'Job failed',
  job_lost: 'Job lost',
  job_canceled: 'Job canceled',
};

/** The actor, in the spelling the jobs screen already established, falling
 *  back to the event's `actor_kind` (a sweep, a chain) when no human acted. */
const actorText = (event: TimelineEvent): string => {
  if (event.actor != null && event.actor !== '') {
    return describeActor(event.actor);
  }
  // A sweep, a chain or an import has no user; naming the mechanism is more
  // honest than "not recorded", which reads as a gap in the audit.
  return event.actor_kind || 'no recorded actor';
};

type Facts = Record<string, unknown>;

const text = (value: unknown): string => (value == null ? '' : String(value));

/**
 * The activation is the event this screen is opened for, so its headline is
 * spelled out rather than left inside a JSON blob: whether a human waved a
 * failing gate through, the sentence they had to type, and which checks were
 * failing when they did. The whole snapshot still rides below in the facts.
 */
const ActivationHeadline: React.FC<{ facts: Facts }> = ({ facts }) => {
  if (facts.snapshot_recorded === false) {
    return (
      <Alert
        className="assessment-callout"
        type="warning"
        showIcon
        title="No verdict was recorded for this activation"
        description="It was activated before activations were recorded, so what it was signed off against cannot be recovered."
      />
    );
  }
  const failed = Array.isArray(facts.failed_checks)
    ? (facts.failed_checks as unknown[]).map(text)
    : [];
  return (
    <>
      {Boolean(facts.overridden) && (
        <Alert
          className="assessment-callout"
          type="warning"
          showIcon
          title="A human overrode a failing validity gate to activate this answer key"
          description={
            <Space direction="vertical" size={2}>
              <Typography.Text>
                {`${
                  text(facts.overridden_by) || 'Someone'
                } overrode verdict "${text(
                  facts.verdict,
                )}", graded ${formatDateTime(text(facts.computed_at))}.`}
              </Typography.Text>
              <Typography.Text italic>
                {`“${text(facts.override_reason)}”`}
              </Typography.Text>
            </Space>
          }
        />
      )}
      {failed.length > 0 && (
        <div>
          <Typography.Text type="secondary">
            Checks that were failing:{' '}
          </Typography.Text>
          <Typography.Text code>{failed.join(', ')}</Typography.Text>
        </div>
      )}
    </>
  );
};

/** Where a mint's run came from. A mint the command line drove has no run at
 *  all, and one recorded before the ledger existed is matched back to the
 *  apply job that named the version it wrote — a different fact from a mint
 *  that stamped its own, so the screen says which rather than implying the
 *  trail is complete. */
const MINT_JOB_SOURCE: Record<string, string> = {
  job_ledger: 'Run matched from the job ledger, not stamped by the mint',
  unrecorded: 'No run is recorded for this mint',
};

const EventBody: React.FC<{ event: TimelineEvent }> = ({ event }) => {
  const facts = (event.facts || {}) as Facts;
  const approximate = facts.at_is_approximate === true;
  const mintSource =
    event.kind === 'mint'
      ? MINT_JOB_SOURCE[text(facts.job_id_source)]
      : undefined;
  return (
    <div>
      <Space size={[8, 4]} wrap>
        <Typography.Text strong>{event.title}</Typography.Text>
        <Tag>{KIND_LABEL[event.kind] || event.kind}</Tag>
      </Space>
      <div>
        <Typography.Text type="secondary">
          {formatDateTime(event.at)}
          {approximate ? ' (approximate)' : ''} · {actorText(event)}
        </Typography.Text>
      </div>
      {approximate && (
        <div>
          {/* An approximate stamp that does not say what it read is a stamp
              nobody can check. */}
          <Typography.Text type="warning">
            {`Read from ${
              text(facts.at_source) || 'a nearby column'
            }; no run recorded this action.`}
          </Typography.Text>
        </div>
      )}
      {mintSource && (
        <div>
          <Typography.Text type="secondary">{mintSource}</Typography.Text>
        </div>
      )}
      {event.kind === 'scenario_activated' && (
        <ActivationHeadline facts={facts} />
      )}
      {Object.keys(facts).length > 0 && <JsonBlock compact value={facts} />}
      <Space size={[8, 8]} wrap>
        {event.link && (
          <Link to={event.link}>
            <Button size="small">Open</Button>
          </Link>
        )}
        {event.job_id && (
          <Link
            to={`/dashboard/scenario-builder?job=${encodeURIComponent(
              event.job_id,
            )}`}
          >
            <Button size="small">Open the job</Button>
          </Link>
        )}
      </Space>
    </div>
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
  blueprint?: number | string;
  draft?: number;
  title?: string;
};

const Timeline: React.FC<Props> = ({
  open,
  onClose,
  blueprint,
  draft,
  title,
}) => {
  const [audit, setAudit] = useState<TimelineDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newestFirst, setNewestFirst] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setAudit(
        await fetchTimeline(
          blueprint != null ? { blueprint } : { draft: draft as number },
        ),
      );
    } catch (requestError) {
      setError(readableError(requestError, { what: 'This timeline' }));
      setAudit(null);
    } finally {
      setLoading(false);
    }
  }, [blueprint, draft]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const events = useMemo(() => {
    const rows = [...(audit?.events || [])];
    rows.sort((a, b) => String(a.at).localeCompare(String(b.at)));
    return newestFirst ? rows.reverse() : rows;
  }, [audit, newestFirst]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={840}
      title={title || 'Timeline'}
      destroyOnHidden
      extra={
        <Space>
          <Select
            aria-label="Timeline order"
            style={{ width: 160 }}
            value={newestFirst ? 'newest' : 'oldest'}
            onChange={value => setNewestFirst(value === 'newest')}
            options={[
              { value: 'newest', label: 'Newest first' },
              { value: 'oldest', label: 'Oldest first' },
            ]}
          />
          <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
            Refresh
          </Button>
        </Space>
      }
    >
      {error && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={error}
        />
      )}

      {(audit?.degraded || []).map((entry, index) => (
        <Alert
          key={index}
          className="assessment-callout"
          type="warning"
          showIcon
          title={`Part of this history could not be read: ${entry.what}`}
          description={entry.why}
        />
      ))}

      {loading && !audit ? (
        <div className="assessment-loading">
          <Spin tip="Loading the timeline" />
        </div>
      ) : events.length ? (
        <Card size="small" title={`${events.length} recorded events`}>
          <AntTimeline
            items={events.map((event, index) => ({
              key: index,
              color: SEVERITY_COLOR[event.severity || 'ok'] || 'blue',
              children: <EventBody event={event} />,
            }))}
          />
        </Card>
      ) : (
        !error && (
          <Alert
            className="assessment-callout"
            type="info"
            showIcon
            title="No recorded history"
            description="Nothing has been captured, minted, pushed or activated for this family yet."
          />
        )
      )}
    </Drawer>
  );
};

export default Timeline;
